import React, { useState, useEffect } from 'react';
import { API_URL } from '../components/config.js';
import axios from 'axios';
import './DataTypeManagementPage.css';

/**
 * DataTypeManagementPage Component
 * 
 * Admin panel for managing data collection types.
 * Allows creating, editing, and configuring data types dynamically.
 */
const DataTypeManagementPage = () => {
  const [dataTypes, setDataTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedDataType, setSelectedDataType] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [stats, setStats] = useState({});
  const [modalShake, setModalShake] = useState(false);

  const handleBackdropClick = () => {
    setModalShake(true);
    setTimeout(() => setModalShake(false), 500);
  };

  // Form state for create/edit
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    description: '',
    category: 'other',
    icon: '📍',
    color: '#4A90E2',
    isActive: true,
    isPublic: true,
    permissions: {
      view: ['public'],
      create: ['user', 'staff', 'admin'],
      edit: ['self', 'staff', 'admin'],
      delete: ['staff', 'admin'],
      comment: ['public']
    },
    iconPalette: {
      enabled: true,
      mode: 'single',
      categories: []
    },
    structuredFields: [],
    freeTextFields: [],
    fileUpload: {
      enabled: true,
      maxFiles: 5,
      allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'application/pdf', 'video/mp4'],
      maxSizeBytes: 10485760
    },
    mapConfig: {
      allowedGeometryTypes: ['Point', 'Polygon'],
      requiresLocation: true,
      clusterMarkers: false
    },
    requiresApproval: true,
    autoApproveRoles: ['staff', 'admin'],
    displayOrder: 0
  });

  useEffect(() => {
    loadDataTypes();
  }, []);

  const loadDataTypes = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        setError('You must be logged in to access this page');
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API_URL}/api/admin/data-types`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDataTypes(response.data.dataTypes);

      // Load stats for each data type
      const statsData = {};
      for (const dt of response.data.dataTypes) {
        const statsRes = await axios.get(`${API_URL}/api/admin/data-types/${dt.id}/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        statsData[dt.id] = statsRes.data.stats;
      }
      setStats(statsData);

    } catch (err) {
      console.error('Error loading data types:', err);
      setError(err.response?.data?.error || 'Error loading data types');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDataType = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('authToken');
      
      await axios.post(`${API_URL}/api/admin/data-types`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setShowCreateForm(false);
      resetForm();
      loadDataTypes();
    } catch (err) {
      console.error('Error creating data type:', err);
      alert(err.response?.data?.error || err.response?.data?.details?.join(', ') || 'Error creating data type');
    }
  };

  const handleUpdateDataType = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('authToken');
      await axios.put(`${API_URL}/api/admin/data-types/${selectedDataType.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSelectedDataType(null);
      resetForm();
      loadDataTypes();
    } catch (err) {
      console.error('Error updating data type:', err);
      alert(err.response?.data?.error || 'Error updating data type');
    }
  };

  const handleToggleStatus = async (dataTypeId) => {
    try {
      const token = localStorage.getItem('authToken');
      await axios.patch(`${API_URL}/api/admin/data-types/${dataTypeId}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadDataTypes();
    } catch (err) {
      console.error('Error toggling status:', err);
      alert(err.response?.data?.error || 'Error toggling status');
    }
  };

  const handleDeleteDataType = async (dataTypeId) => {
    if (!window.confirm('Are you sure you want to delete this data type? This cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      await axios.delete(`${API_URL}/api/admin/data-types/${dataTypeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadDataTypes();
    } catch (err) {
      console.error('Error deleting data type:', err);
      alert(err.response?.data?.error || 'Error deleting data type');
    }
  };

  const handleEdit = (dataType) => {
    setSelectedDataType(dataType);
    // Merge with defaults to ensure all fields are defined
    setFormData({
      id: dataType.id || '',
      name: dataType.name || '',
      description: dataType.description || '',
      category: dataType.category || 'other',
      icon: dataType.icon || '📍',
      color: dataType.color || '#4A90E2',
      isActive: dataType.isActive !== undefined ? dataType.isActive : true,
      isPublic: dataType.isPublic !== undefined ? dataType.isPublic : true,
      permissions: dataType.permissions || {
        view: ['public'],
        create: ['user', 'staff', 'admin'],
        edit: ['self', 'staff', 'admin'],
        delete: ['staff', 'admin'],
        comment: ['public']
      },
      iconPalette: dataType.iconPalette || {
        enabled: true,
        mode: 'single',
        categories: []
      },
      structuredFields: dataType.structuredFields || [],
      freeTextFields: dataType.freeTextFields || [],
      fileUpload: dataType.fileUpload || {
        enabled: true,
        maxFiles: 5,
        allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'application/pdf', 'video/mp4'],
        maxSizeBytes: 10485760
      },
      mapConfig: dataType.mapConfig || {
        allowedGeometryTypes: ['Point', 'Polygon'],
        requiresLocation: true,
        clusterMarkers: false
      },
      requiresApproval: dataType.requiresApproval !== undefined ? dataType.requiresApproval : true,
      autoApproveRoles: dataType.autoApproveRoles || ['staff', 'admin'],
      displayOrder: dataType.displayOrder || 0
    });
    setShowCreateForm(false);
  };

  const resetForm = () => {
    setFormData({
      id: '',
      name: '',
      description: '',
      category: 'other',
      icon: '📍',
      color: '#4A90E2',
      isActive: true,
      isPublic: true,
      permissions: {
        view: ['public'],
        create: ['user', 'staff', 'admin'],
        edit: ['self', 'staff', 'admin'],
        delete: ['staff', 'admin'],
        comment: ['public']
      },
      iconPalette: {
        enabled: true,
        mode: 'single',
        categories: []
      },
      structuredFields: [],
      freeTextFields: [],
      fileUpload: {
        enabled: true,
        maxFiles: 5,
        allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'application/pdf', 'video/mp4'],
        maxSizeBytes: 10485760
      },
      mapConfig: {
        allowedGeometryTypes: ['Point', 'Polygon'],
        requiresLocation: true,
        clusterMarkers: false
      },
      requiresApproval: true,
      autoApproveRoles: ['staff', 'admin'],
      displayOrder: 0
    });
    setSelectedDataType(null);
    setShowCreateForm(false);
  };

  if (loading) {
    return <div className="data-type-management-page"><div className="loading">Loading...</div></div>;
  }

  return (
    <div className="data-type-management-page">
      <div className="page-header">
        <h2>🎯 Data Type Management</h2>
        <p>Configure collection types, fields, icons, and permissions</p>
        <button className="btn-primary" onClick={() => setShowCreateForm(true)}>
          + Create New Data Type
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Data Types List */}
      <div className="data-types-grid">
        {dataTypes.map(dataType => (
          <div key={dataType.id} className={`data-type-card ${!dataType.isActive ? 'inactive' : ''}`}>
            <div className="card-header">
              <div className="type-info">
                <span className="type-icon" style={{ fontSize: '2rem' }}>{dataType.icon}</span>
                <div>
                  <h3>{dataType.name}</h3>
                  <span className="type-id">{dataType.id}</span>
                </div>
              </div>
              <div className="status-badges">
                {!dataType.isActive && <span className="badge badge-inactive">Inactive</span>}
                {!dataType.isPublic && <span className="badge badge-private">Private</span>}
                <span className="badge" style={{ backgroundColor: dataType.color }}>{dataType.category}</span>
              </div>
            </div>

            <p className="type-description">{dataType.description}</p>

            {stats[dataType.id] && (
              <div className="type-stats">
                <div className="stat-item">
                  <span className="stat-label">Total Items:</span>
                  <span className="stat-value">{stats[dataType.id].total}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Pending:</span>
                  <span className="stat-value">{stats[dataType.id].pending}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Approved:</span>
                  <span className="stat-value">{stats[dataType.id].approved}</span>
                </div>
              </div>
            )}

            <div className="type-config-summary">
              <div><strong>Structured Fields:</strong> {dataType.structuredFields?.length || 0}</div>
              <div><strong>Free-text Fields:</strong> {dataType.freeTextFields?.length || 0}</div>
              <div><strong>Icon Categories:</strong> {dataType.iconPalette?.categories?.length || 0}</div>
            </div>

            <div className="card-actions">
              <button className="btn-small btn-edit" onClick={() => handleEdit(dataType)}>
                Edit Config
              </button>
              <button 
                className={`btn-small ${dataType.isActive ? 'btn-warning' : 'btn-success'}`}
                onClick={() => handleToggleStatus(dataType.id)}
              >
                {dataType.isActive ? 'Deactivate' : 'Activate'}
              </button>
              <button 
                className="btn-small btn-delete" 
                onClick={() => handleDeleteDataType(dataType.id)}
                disabled={stats[dataType.id]?.total > 0}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Create/Edit Form Modal */}
      {(showCreateForm || selectedDataType) && (
        <div className="modal-overlay" onClick={handleBackdropClick}>
          <div className={`modal-content large${modalShake ? ' modal-shake' : ''}`} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{selectedDataType ? 'Edit Data Type' : 'Create New Data Type'}</h3>
              <button className="modal-close" onClick={resetForm}>×</button>
            </div>

            <form onSubmit={selectedDataType ? handleUpdateDataType : handleCreateDataType}>
              <div className="form-grid">
                <div className="form-group">
                  <label>ID (lowercase, no spaces)</label>
                  <input
                    type="text"
                    value={formData.id}
                    onChange={(e) => setFormData({ ...formData, id: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                    required
                    disabled={!!selectedDataType}
                    placeholder="e.g., fish-harvesting"
                  />
                  <small style={{ color: '#666', fontSize: '0.85rem' }}>Only lowercase letters, numbers, and hyphens</small>
                </div>

                <div className="form-group">
                  <label>Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Icon (emoji)</label>
                  <input
                    type="text"
                    value={formData.icon}
                    onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                    required
                    maxLength="2"
                  />
                  <small style={{ color: '#666', fontSize: '0.85rem' }}>
                    Common emojis: 🌿 🦌 🦅 🎣 🫐 🍄 🐟 🌲 🏞️ ⚠️ 🔥 🌊 📍 🗺️
                  </small>
                </div>

                <div className="form-group">
                  <label>Color</label>
                  <input
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    required
                  >
                    <option value="planning">Planning</option>
                    <option value="environment">Environment</option>
                    <option value="wildlife">Wildlife</option>
                    <option value="harvesting">Harvesting</option>
                    <option value="hazards">Hazards</option>
                    <option value="cultural">Cultural</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Display Order</label>
                  <input
                    type="number"
                    value={formData.displayOrder || 0}
                    onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows="3"
                />
              </div>

              <div className="form-checkboxes">
                <label>
                  <input
                    type="checkbox"
                    checked={!!formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                  Active
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={!!formData.isPublic}
                    onChange={(e) => setFormData({ ...formData, isPublic: e.target.checked })}
                  />
                  Public
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={formData.requiresApproval}
                    onChange={(e) => setFormData({ ...formData, requiresApproval: e.target.checked })}
                  />
                  Requires Approval
                </label>
              </div>

              {/* Icon Palette Builder */}
              <div className="form-section">
                <h4>📍 Icon Palette (Optional)</h4>
                <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '1rem' }}>
                  Add categories with different icons that users can select when creating entries. 
                  Leave empty if you want all entries to use the same icon.
                </p>
                
                <div className="icon-palette-builder">
                  {formData.iconPalette?.categories && formData.iconPalette.categories.length > 0 ? (
                    formData.iconPalette.categories.map((cat, idx) => (
                      <div key={idx} className="palette-item">
                        <input
                          type="text"
                          placeholder="Category name"
                          value={cat.name || ''}
                          onChange={(e) => {
                            const newCategories = [...formData.iconPalette.categories];
                            newCategories[idx] = { ...newCategories[idx], name: e.target.value };
                            setFormData({
                              ...formData,
                              iconPalette: { ...formData.iconPalette, categories: newCategories }
                            });
                          }}
                          style={{ flex: 2 }}
                        />
                        <input
                          type="text"
                          placeholder="Icon"
                          value={cat.icons?.[0]?.icon || ''}
                          maxLength="2"
                          onChange={(e) => {
                            const newCategories = [...formData.iconPalette.categories];
                            newCategories[idx] = {
                              ...newCategories[idx],
                              icons: [{ icon: e.target.value, name: e.target.value }]
                            };
                            setFormData({
                              ...formData,
                              iconPalette: { ...formData.iconPalette, categories: newCategories }
                            });
                          }}
                          style={{ flex: 1, fontSize: '1.5rem', textAlign: 'center' }}
                        />
                        <button
                          type="button"
                          className="btn-small btn-delete"
                          onClick={() => {
                            const newCategories = formData.iconPalette.categories.filter((_, i) => i !== idx);
                            setFormData({
                              ...formData,
                              iconPalette: { ...formData.iconPalette, categories: newCategories }
                            });
                          }}
                        >
                          🗑️
                        </button>
                      </div>
                    ))
                  ) : (
                    <p style={{ color: '#999', fontStyle: 'italic' }}>No categories added yet</p>
                  )}
                  
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => {
                      const newCategories = [
                        ...(formData.iconPalette?.categories || []),
                        { name: '', icons: [{ icon: '📍', name: '📍' }] }
                      ];
                      setFormData({
                        ...formData,
                        iconPalette: {
                          ...formData.iconPalette,
                          enabled: true,
                          categories: newCategories
                        }
                      });
                    }}
                    style={{ marginTop: '0.5rem' }}
                  >
                    + Add Category
                  </button>
                </div>
                
                <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#f8f9fa', borderRadius: '4px' }}>
                  <small style={{ color: '#666' }}>
                    <strong>Quick Icons:</strong> 🌿 🦌 🦅 🎣 🫐 🍄 🐟 🌲 🏞️ ⚠️ 🔥 🌊 📍 🗺️ 🏔️ 🌾 🦫 🐻 🦅 🦆
                  </small>
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-primary">
                  {selectedDataType ? 'Update' : 'Create'} Data Type
                </button>
                <button type="button" className="btn-secondary" onClick={resetForm}>
                  Cancel
                </button>
              </div>

              <div className="config-note">
                <p>
                  ℹ️ <strong>Note:</strong> Advanced configuration (fields, icon palettes, permissions) 
                  will be available in a separate editor after creating the data type.
                </p>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTypeManagementPage;
